import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE_CONTACTS } from '../partials/Header';
import { PRIVACY_PATH } from '../../legal/legalDocuments';

const emptyDraft = () => ({ name: '', email: '', message: '' });

export default function ContactSection() {
    const id = useId();
    const pendingRequest = useRef(null);
    const [draft, setDraft] = useState(emptyDraft);
    const [status, setStatus] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => () => pendingRequest.current?.abort(), []);

    function updateDraft(event) {
        const { name, value } = event.target;
        event.target.setCustomValidity('');
        setDraft(previous => ({ ...previous, [name]: value }));
        setStatus(null);
    }

    async function handleSubmit(event) {
        event.preventDefault();
        if (pendingRequest.current) return;

        const form = event.currentTarget;
        for (const fieldName of ['name', 'email', 'message']) {
            if (!draft[fieldName].trim()) {
                const field = form.elements.namedItem(fieldName);
                field.setCustomValidity('Compila questo campo.');
                field.reportValidity();
                return;
            }
        }
        if (!form.reportValidity()) return;

        const apiUrl = process.env.REACT_APP_AUTH_API_URL?.trim();
        if (!apiUrl) {
            setStatus({ type: 'error', message: `Il servizio di invio non è al momento disponibile. Scrivici a ${SITE_CONTACTS.email}.` });
            return;
        }

        const controller = new AbortController();
        let timedOut = false;
        const timeout = window.setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, 20000);
        pendingRequest.current = controller;
        setSubmitting(true);
        setStatus(null);

        try {
            const response = await fetch(`${apiUrl.replace(/\/$/, '')}/send-mail`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal,
                body: JSON.stringify({
                    name: draft.name.trim(),
                    email: draft.email.trim(),
                    phone: '',
                    message: draft.message.trim(),
                }),
            });
            if (!response.ok) throw new Error('Message submission failed');

            setDraft(emptyDraft());
            setStatus({ type: 'success', message: 'Messaggio inviato con successo. Ti risponderemo al più presto.' });
        } catch (error) {
            setStatus({
                type: 'error',
                message: timedOut
                    ? 'L’invio sta richiedendo troppo tempo. Riprova tra qualche istante.'
                    : `Non è stato possibile inviare il messaggio. Riprova o scrivici a ${SITE_CONTACTS.email}.`,
            });
        } finally {
            window.clearTimeout(timeout);
            pendingRequest.current = null;
            setSubmitting(false);
        }
    }

    return (
        <section id="contatti" className="ef-contact" aria-labelledby={`${id}-heading`}>
            <div className="ef-container">
                <div className="ef-section-heading ef-contact-intro">
                    <p className="ef-eyebrow">Contatti</p>
                    <h2 id={`${id}-heading`} className="ef-section-title">Scrivici. <em>Siamo qui.</em></h2>
                    <p>Hai bisogno di informazioni sui servizi funebri o vuoi entrare nella rete Easy Funeral? Raccontaci la tua richiesta: ti risponderemo via email al più presto.</p>
                </div>

                <form className="ef-contact-form" onSubmit={handleSubmit} aria-busy={submitting}>
                    <div className="ef-field">
                        <label htmlFor={`${id}-name`}>Nome o nome agenzia</label>
                        <input id={`${id}-name`} name="name" type="text" autoComplete="name" placeholder="Come possiamo chiamarti?" value={draft.name} onChange={updateDraft} disabled={submitting} required />
                    </div>
                    <div className="ef-field">
                        <label htmlFor={`${id}-email`}>Email</label>
                        <input id={`${id}-email`} name="email" type="email" autoComplete="email" placeholder="info@esempio.it" value={draft.email} onChange={updateDraft} disabled={submitting} required />
                    </div>
                    <div className="ef-field">
                        <label htmlFor={`${id}-message`}>Come possiamo aiutarti?</label>
                        <textarea id={`${id}-message`} name="message" rows={5} placeholder="Scrivi qui la tua richiesta..." value={draft.message} onChange={updateDraft} disabled={submitting} required />
                    </div>
                    <p className="ef-privacy">
                        <span>Usiamo nome, email e messaggio per rispondere alla tua richiesta, come descritto nell’<Link to={PRIVACY_PATH} target="_blank" rel="noopener noreferrer">informativa sul trattamento dei dati personali</Link>. Inserisci solo i dati necessari: non inviare dati sanitari né documenti dei defunti o delle famiglie.</span>
                    </p>
                    <button type="submit" className="ef-button ef-button--primary" disabled={submitting}>
                        {submitting ? 'Invio in corso...' : 'Invia messaggio'}
                    </button>
                    {status && (
                        <div className={`ef-form-status ef-form-status--${status.type}`} role="status" aria-live="polite" aria-atomic="true">
                            {status.message}
                        </div>
                    )}
                </form>
            </div>
        </section>
    );
}
